// The Daily Intention Selector's authored content and the local per-day
// persistence store. One intention per day; rolls over at the member's
// local midnight. Nothing here is on the backend — this is intentionally a
// per-device, quiet local choice.

export const INTENTION_CATEGORIES = [
  {
    slug: "peace",
    title: "Peace & Emotional Regulation",
    intentions: [
      "Today, I pause before I react — one breath is enough.",
      "Today, I feel what I feel without making it a problem.",
      "Today, I let emotions move through me rather than run me.",
      "Today, I do not carry storms that were never mine to hold.",
      "Today, I choose calm as my baseline, not my reward.",
    ],
  },
  {
    slug: "self-love",
    title: "Self-Love & Worthiness",
    intentions: [
      "Today, I honor myself with compassion and patience.",
      "Today, I give myself permission to grow and rest.",
      "Today, I choose confidence and self-respect.",
      "Today, I nourish my soul through aligned action.",
      "Today, I honor my body, my mind, and my longevity.",
    ],
  },
  {
    slug: "abundance",
    title: "Abundance & Receiving",
    intentions: [
      "Today, I welcome abundance in all forms.",
      "Today, I allow good things to flow toward me.",
      "Today, I trust the timing of my life.",
      "Today, I choose abundance over limitation.",
      "Today, I move with intention and clarity.",
    ],
  },
  {
    slug: "alignment",
    title: "Alignment & Purpose",
    intentions: [
      "Today, I let my future self make my decisions for me.",
      "Today, I protect my energy for the work that actually matters.",
      "Today, I say no to what pulls me off my path — without guilt.",
      "Today, I take one small action toward the life I'm building.",
      "Today, I let discipline be an act of self-love, not pressure.",
    ],
  },
  {
    slug: "aligned-love",
    title: "Attracting Aligned Love",
    intentions: [
      "Today, I embody the energy of the partnership I want to attract.",
      "Today, I release what does not honor me to make room for what does.",
      "Today, I choose the presence of my own company over unaligned attention.",
      "Today, I let my standards protect me — I do not soften them to be chosen.",
      "Today, I trust divine timing and stay open, patient, and unhurried.",
    ],
  },
];

const KEY = "n8one.daily-intention.v1";

function todayIso() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function safeParse(raw) {
  try { return raw ? JSON.parse(raw) : null; } catch { return null; }
}

/**
 * Return today's intention, or `null` if the member has not selected one yet
 * for the current local date. Automatically discards a stale intention from
 * a previous day so nothing lingers past midnight.
 */
export function getTodaysIntention() {
  if (typeof window === "undefined") return null;
  const p = safeParse(window.localStorage.getItem(KEY));
  if (!p || !p.text || !p.date) return null;
  if (p.date !== todayIso()) {
    try { window.localStorage.removeItem(KEY); } catch { /* noop */ }
    return null;
  }
  return p;
}

/** Save today's intention. Broadcasts a window event so any listening surface updates. */
export function setTodaysIntention({ text, category = "", categoryTitle = "" }) {
  if (typeof window === "undefined") return null;
  const clean = (text || "").trim();
  if (!clean) return null;
  const payload = {
    text: clean,
    category,
    category_title: categoryTitle,
    date: todayIso(),
    set_at: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(KEY, JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent("daily-intention-changed"));
  } catch { /* noop */ }
  return payload;
}

/** Release today's intention. */
export function clearTodaysIntention() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY);
    window.dispatchEvent(new CustomEvent("daily-intention-changed"));
  } catch { /* noop */ }
}
